//internal imports
import React, {useCallback, useContext, useEffect, useRef} from 'react';
import {Alert, Modal, Progress, Spin} from 'antd';
// external imports
import {useAuth0} from '@auth0/auth0-react';
import {useQuery} from '@apollo/react-hooks';
// custom imports
import {GET_DASHBOARD_ITEMS} from '../graphql/queries';
import FilterComponent from '../components/DashboardFilters/FilterComponent'
import NotAuthenticated from '../components/auth0/NotAuthenticated';
import ChartRenderer from '../components/ChartRenderer';
import Dashboard from '../components/Dashboard';
import DashboardItem from '../components/DashboardItem';
import AppContext from '../context/AppContext'
import chart from '../utils/chart'
import time from '../utils/time'
import dashboardFilter from '../utils/dashboardFilter';
import dashboardClient from '../utils/dashboardClient'
import useQueryReducer from '../hooks/useQueryReducer'
import useQueryUpdater from '../hooks/useQueryUpdater'

const deserializeItem = (i) => ({
  ...i,
  layout: JSON.parse(i.layout) || {},
  vizState: JSON.parse(i.vizState),
});

const defaultLayout = (i) => ({
  x: i.layout.x || 0,
  y: i.layout.y || 0,
  w: i.layout.w || 4,
  h: i.layout.h || 8,
  minW: 4,
  minH: 4,
});

const styles = {
  empty: () => ({
    textAlign: 'center',
    padding: 12,
  }),
  load: () => ({
    display: 'flex',
    justifyContent: 'center',
    alignItems: "center",
    height: '100vh',
    backgroundColor: 'white'
  })
}

const resetRenderedCharts = (renderedCharts) => {
  Object.keys(renderedCharts.current).map((key) => renderedCharts.current[key] = false)
}

function DashboardPage() {

  //useRefs
  const firstLoad = useRef(false)


  /**
   * object that indicates whether a chart is ready or not ({chartId: true | false})
   * it's filled in the chart components and used in the export function
   * @type {React.MutableRefObject<{}>}
   */
  const renderedCharts = useRef({})

  // auth0
  const { isAuthenticated, isLoading } = useAuth0();


  // useQuery: query graphql to get dashboard items
  const { loading, error, data, startPolling, stopPolling } = useQuery(
      GET_DASHBOARD_ITEMS,
      {
        fetchPolicy: 'cache-and-network'
      }
  );


  // useQueryReducer custom hook
  const { initialState, state, dispatch, clearAllFilters } = useQueryReducer();

  //useQueryUpdater custom hook
  const queryUpdater = useQueryUpdater()
  const updateDashboardItem = async (dashboardItem) => {
    await (await queryUpdater).updateQuery(dashboardItem)
  }


  // useContext
  const {
    update,
    setUpdate,
    allExport,
    setAllExport,
    exportProgress,
    setExportProgress,
    clientName
  } = useContext(AppContext)


  // useCallbacks
  const onFilterChanged = useCallback((key, value) => {
    let newItem = {
      [`${key}`]: value
    }
    // newItem[key] = value
    dispatch(newItem)
  })

  /**
   * update all dashboard items on filtering / reset
   * @param isReset
   */
  const updateDashboard = (isReset = false) => {
    const fState = dashboardFilter.getFilterState()
    // console.log("update dashboard function", fState)
    data.dashboardItems.forEach((dashboardItem) => {
      let filters = isReset ? [] : dashboardFilter.mapValueToFilter(dashboardItem, fState, clientName)
      let timeDimensions = time.mapDateRangeToTimeDimensions(dashboardItem, fState.dateRange, 'date')
      let vizState = JSON.parse(dashboardItem.vizState)
      vizState.query.filters = filters
      vizState.query.timeDimensions = timeDimensions
      let updatedSegments = vizState.query.segments.filter(segment =>
          !segment.includes("Client")
      )
      updatedSegments.push(
          dashboardClient.mapClientToSegment(chart.getDashboardItemAttribute(dashboardItem.id, "tableName"), clientName, fState?.expoCountry)
      )
      vizState.query.segments = updatedSegments

      updateDashboardItem({
        ...dashboardItem,
        vizState: JSON.stringify(vizState)
      })
    })
  }

  /**
   * displays data based on selected client
   * called on first render and when the client is changed
   * @type {(function(*=): void)|*}
   */
  const handleClientChange = useCallback((resetFilters = true) => {
    if (resetFilters) clearAllFilters()
    const currentDashboardItems = chart.getDashboardItemsForClient(clientName)
    chart.setLocalDashboardItems(currentDashboardItems.dashboardItems)
    currentDashboardItems.dashboardItems.forEach((dashItem) => {
      let vizState = JSON.parse(dashItem.vizState)
      let updatedSegments = vizState.query.segments.filter(segment =>
          !segment.includes("Client")
      )
      updatedSegments.push(
          dashboardClient.mapClientToSegment(dashItem.tableName, clientName, state?.expoCountry)
      )
      vizState.query.segments = updatedSegments
      vizState.query.timeDimensions = time.mapDateRangeToTimeDimensions(dashItem, dashboardFilter.getFilterState().dateRange, 'date')
      let layout = JSON.parse(dashItem.layout)
      if (dashItem.id === '4') {
        layout.w = clientName === 'fii' ? 12 : 8
      }
      updateDashboardItem({
        ...dashItem,
        vizState: JSON.stringify(vizState),
        layout: JSON.stringify(layout)
      })
    })
  }, [clientName, state.expoCountry])

  const onUploadComplete = useCallback(() => {
    setUpdate(true)
    startPolling(2000)

    setTimeout(() => {
      stopPolling()
      setUpdate(false)
    }, 30000)
  })

  const onApplyClicked = useCallback(() => {
    dashboardFilter.setFilterState(state)
    updateDashboard()
  })

  const onResetAllClicked = useCallback(() => {
    clearAllFilters()
    dashboardFilter.setFilterState(initialState)
    updateDashboard(true)
  })

  /**
   * checks if current dashboard charts are ready within 2 seconds intervals
   * @returns {Promise<unknown>}
   */
  const waitForCharts = useCallback(async () => {
    return new Promise((resolve) => {
      const intervalId = setInterval(() => {
        const localDashItems = chart.getLocalDashboardItems()
        const arr = localDashItems.reduce((next, dashItem) => {
          const id = dashItem.id
          return !!renderedCharts.current[id] && next
        }, true)
        // console.log('2s:' ,arr, renderedCharts.current)
        if (arr === true) {
          clearInterval(intervalId);
          resolve(true)
        }
      }, 2000)
    }).then((r) => {
      // console.log(r, renderedCharts.current)
    })
  })

  const onExportConfirmed = useCallback(async (exportOption) => {
    let progress = 0
    const reportId = chart.getReportId()
    console.log("reportId", reportId)
    console.log("export option", exportOption)

    setExportProgress({
      ...exportProgress,
      status: 'active',
      progress,
      message: 'Exporting charts'
    })
    setAllExport(true)

    await new Promise((resolve) => setTimeout(resolve, 100))

    try {
      if (clientName == "expo" && exportOption.exportOption == "each") {

        const expoCountries = dashboardFilter.getDimensionOption("expoCountry")?.options

        for (var i = 0; i < expoCountries.length; i++) {
          console.log("change country", expoCountries[i])
          onFilterChanged("expoCountry", expoCountries[i].value)
          console.log("get new dashboard")
          dashboardFilter.setFilterState({
            ...state,
            expoCountry: expoCountries[i].value
          })
          updateDashboard(false)

          // check if all charts rendered every two seconds
          await waitForCharts()
          let success = await chart.uploadToGCP(reportId, clientName, expoCountries[i].value)

          progress = (((i + 1) * 100) / (expoCountries.length + 2)).toFixed(1)
          setExportProgress({
            ...exportProgress,
            progress,
            message: 'Exporting charts',
            status: 'active',
          })
          if (!success) throw new Error("Error exporting charts")
          // else
          //     // on success reset rendered charts obj
          //   resetRenderedCharts(renderedCharts)
        }
      } else {
        await waitForCharts()
        let success = await chart.uploadToGCP(reportId, clientName)
        progress = (100 / 3).toFixed(1)
        setExportProgress({
          ...exportProgress,
          progress,
          message: 'Exporting charts',
          status: 'active',
        })
        if (!success) throw new Error("Error exporting charts")
        // else
        //   resetRenderedCharts(renderedCharts)
      }

      const configSuccess = await chart.uploadConfig(reportId, clientName, exportOption.defaultLayout)
      if (!configSuccess) throw new Error("Error exporting configurations")
      setExportProgress({
        ...exportProgress,
        message: 'Exporting charts',
        status: 'active',
        progress: (200 / 3).toFixed(1)
      })
      await chart.downloadReport(reportId, clientName, exportProgress, setExportProgress)
    } catch (err) {
      console.log(err.message)
      setExportProgress({
        ...exportProgress,
        status: "exception",
        message: err.message

      })
    }

    chart.removeReportId()

  })

  /**
   * stores dashboard info locally
   * @type {(function(*): void)|*}
   */
  const setDashboardInfo = useCallback((data) => {
    const dashboardItems = data.dashboardItems.map(dashboardItem => {
      let vizState = JSON.parse(dashboardItem.vizState)
      vizState.timeDimensions = time.mapDateRangeToTimeDimensions(dashboardItem, dashboardFilter.getFilterState().dateRange, 'date')
      let dashItem = chart.getDashboardItems().dashboardItems.find(dItem => dItem.id == dashboardItem.id)
      return {
        ...dashItem,
        // vizState: dashboardItem.vizState,
        vizState: JSON.stringify(vizState)
      }
    }).filter(dashboardItem => dashboardItem.clientNames.some(cName => cName == clientName))

    chart.setLocalDashboardItems(dashboardItems)

  })

  // useEffects

  /**
   * useEffect called on 1st render
   */
  useEffect(() => {
    chart.setDashboardItems()
    dashboardFilter.setDimensionOptions()
  }, [])
  /**
   * useEffect that renders whenever data is updated
   */
  useEffect(() => {
    resetRenderedCharts(renderedCharts)
    if (data) {
      // console.log("useEffect [data] firstLoad ref", firstLoad.current)
      if (!firstLoad.current) {
        setDashboardInfo(data)
        firstLoad.current = true
      }
    }
    // console.log(data)
  }, [data])
  /**
   * useEffect called when client changes
   */
  useEffect(() => {
    setUpdate(true)
    dashboardClient.setSelectedClient(clientName)
    // setDashboardInfo(data)
    handleClientChange()
    setTimeout(() => { setUpdate(false) }, 1000)
  }, [clientName])

  // functional components
  /**
   * functional component for dashboard chart
   * @function dashboardItem
   * @param {Object} item
   */
  const dashboardItem = (item) => (
      <div key={item.id} data-grid={defaultLayout(item)}>
        <DashboardItem
            key={item.id}
            itemId={item.id}
            title={item.name}
        >
          <ChartRenderer
              id={`${item.id}Chart`}
              vizState={item.vizState}
              amchartId={item.id}
              amchartTitle={item.name}
              clientName={clientName}
              renderedCharts={renderedCharts}
          />
        </DashboardItem>
      </div>
  );

  // if query is loading
  if (loading) {
    return <Spin />;
  }

  // if authentication is in progress
  if (isLoading)
    return (
        <div>
          <div> Authenticating ...</div>
        </div>
    );

  /**
   * functional component to be displayed when there are no dashboard items
   * @function Empty
   */
  const Empty = () => (
      <div
          style={styles.empty()}
      >
        {isAuthenticated && (
            <>
              <h2>There are no charts on this dashboard</h2>
            </>
        )}
      </div>
  );

  // if query is loading
  if (loading) {
    return <Spin />;
  }

  // if authentication is in progress
  if (isLoading)
    return (
        <div>
          <div> Authenticating ...</div>
        </div>
    );

  // if query returns an error
  if (error) {
    return (
        <Alert
            message="Error occurred while loading your query"
            description={error.toString()}
            type="error"
        />
    );
  }

  return (
      <>
        {!isLoading && !isAuthenticated && <NotAuthenticated />}
        {(isAuthenticated && (!data || data.dashboardItems.length)) ? (
            <>
              {!update ?
                  <>
                    <Modal
                        style={{ top: 20 }}
                        closable={exportProgress.status === 'success' || exportProgress.status === 'exception'}
                        visible={allExport}
                        onOk={() => {
                        }}
                        onCancel={() => {
                          setAllExport(false)
                        }}
                        afterClose={() => {
                          setExportProgress({ ...exportProgress, progress: 0, status: 'default' })
                        }}
                        footer={null}
                        destroyOnClose
                    >
                      {exportProgress.message}
                      <Progress percent={exportProgress.progress} status={exportProgress.status} />

                    </Modal>
                    <FilterComponent
                        filterState={state}
                        handleFilterChange={onFilterChanged}
                        dimensionOptions={dashboardFilter.getDimensionOptionsForClient(clientName)}
                        afterUpload={onUploadComplete}
                        handleApply={onApplyClicked}
                        handleResetAll={onResetAllClicked}
                        exportOptions={chart.getExportOptions(clientName, state)}
                        handleExport={onExportConfirmed}
                        clientName={clientName}
                    />
                    <div id="full-dashboard">
                      <Dashboard dashboardItems={data && data.dashboardItems}>
                        {data && data.dashboardItems.map(deserializeItem).map(dashboardItem)}
                      </Dashboard>
                    </div>
                  </> :
                  <div style={styles.load()}>
                    <Spin
                        tip="Please wait while we load the new data"
                        size='large'
                        spinning={update}
                    >
                    </Spin>
                  </div>
              }
            </>
        ) : (
            <Empty />
        )}
      </>)
}

export default DashboardPage;
